import React, { createContext, ReactNode, useEffect, useReducer } from 'react'

import {
  bootstrap,
  ChatHistoryLoadingState,
  ChatMessage,
  Conversation,
  CosmosDBHealth,
  CosmosDBStatus,
  FeedbackType,
  fetchAppSecret,
  FrontendSettings,
  frontendSettings,
  getNotifications,
  historyEnsure,
  historyList,
  Notification,
  User,
} from '../api'

import { isNil } from 'lodash'
import { useNotifications } from '../hooks/useNotifications'
import { extractTruePermissions } from '../utils/permissions'
import { appStateReducer } from './AppReducer'

export const initialExperimentListState = {
  filters: { 'planning_basics.year': '2025' },
  sortBy: 'Code',
  sortOrder: 'desc' as 'asc' | 'desc',
  searchQuery: '',
}

export const initialMicroLearningListState = {
  statusFilter: 'All',
  sortOrder: 'Highest Score',
  searchQuery: '',
  page: 1,
  columnSortBy: '',
  columnSortOrder: 'desc' as 'asc' | 'desc',
  ouFilter: '',
  areaFilter: '',
  glaFilter: '',
  scalePotentialFilter: '' as '' | 'true' | 'false',
}

export const initialRoleChangeRequestFilterState = {
  status: undefined,
  processed_by_user_id: undefined,
  start_date: undefined,
  end_date: undefined,
  ou: undefined,
}

export interface AppState {
  chatHistoryLoadingState: ChatHistoryLoadingState
  isCosmosDBAvailable: CosmosDBHealth
  chatHistory: Conversation[] | null
  filteredChatHistory: Conversation[] | null
  currentChat: Conversation | null
  frontendSettings: FrontendSettings | null
  feedbackState: { [answerId: string]: FeedbackType.Neutral | FeedbackType.Positive | FeedbackType.Negative }
  isLoading: boolean
  answerExecResult: { [answerId: string]: [] }
  shouldValidateForm: boolean
  isUserActive: boolean | null
  homeChatMessage: ChatMessage['content'] | null
  snackbarState: {
    message: string
    severity: 'success' | 'warning' | 'error'
    action?: { label: string; onClick: () => void }
  } | null
  permissions: string[]
  isNewChatDialogOpen: boolean
  isRequestAccessDialogOpen: boolean
  experimentListState: {
    filters: Record<string, any>
    sortBy: string
    sortOrder: 'asc' | 'desc'
    searchQuery: string
  }
  microLearningListState: {
    statusFilter: string
    sortOrder: string
    searchQuery: string
    page: number
    columnSortBy: string
    columnSortOrder: 'asc' | 'desc'
    ouFilter: string
    areaFilter: string
    glaFilter: string
    scalePotentialFilter: '' | 'true' | 'false'
  }
  user: User | null
  changeRoleRequest: {
    organizationName: string
    ou: string
    function: string
    requestReason: string
  } | null
  hasPendingRoleChangeRequest: boolean
  roleChangeRequestFilterState: {
    status: string | undefined
    processed_by_user_id: string | undefined
    start_date: string | undefined
    end_date: string | undefined
  }
  notifications: Notification[] | null
  hasFetchNotificationsError: boolean
  userFilter: { role: string[]; search: string } | null
  userHistoryFilter: {
    search?: string
    action_type?: string
    start_date?: string
    end_date?: string
    processed_by_user_id?: string
  }
  lookups: { ous: string[] }
}

export type Action =
  | { type: 'SET_COSMOSDB_STATUS'; payload: CosmosDBHealth }
  | { type: 'UPDATE_CHAT_HISTORY_LOADING_STATE'; payload: ChatHistoryLoadingState }
  | { type: 'UPDATE_CURRENT_CHAT'; payload: Conversation | null }
  | { type: 'UPDATE_FILTERED_CHAT_HISTORY'; payload: Conversation[] | null }
  | { type: 'UPDATE_CHAT_HISTORY'; payload: Conversation }
  | { type: 'UPDATE_CHAT_TITLE'; payload: Conversation }
  | { type: 'DELETE_CHAT_ENTRY'; payload: string }
  | { type: 'DELETE_CHAT_HISTORY' }
  | { type: 'DELETE_CURRENT_CHAT_MESSAGES'; payload: string }
  | { type: 'FETCH_CHAT_HISTORY'; payload: Conversation[] | null }
  | { type: 'FETCH_FRONTEND_SETTINGS'; payload: FrontendSettings | null }
  | {
      type: 'SET_FEEDBACK_STATE'
      payload: { answerId: string; feedback: FeedbackType.Positive | FeedbackType.Negative | FeedbackType.Neutral }
    }
  | { type: 'GET_FEEDBACK_STATE'; payload: string }
  | { type: 'SET_SHOULD_VALIDATE_FORM'; payload: boolean }
  | { type: 'SET_ANSWER_EXEC_RESULT'; payload: { answerId: string; exec_result: [] } }
  | { type: 'SET_IS_USER_ACTIVE'; payload: boolean }
  | { type: 'SET_HOME_CHAT_MESSAGE'; payload: ChatMessage['content'] | null }
  | { type: 'SET_SNACKBAR_STATE'; payload: { message: string; severity: 'success' | 'warning' | 'error'; action?: { label: string; onClick: () => void } } | null }
  | { type: 'SET_PERMISSIONS'; payload: string[] }
  | { type: 'SET_IS_NEW_CHAT_DIALOG_OPEN'; payload: boolean }
  | { type: 'SET_IS_REQUEST_ACCESS_DIALOG_OPEN'; payload: boolean }
  | {
      type: 'SET_EXPERIMENT_LIST_STATE'
      payload: { filters: Record<string, any>; sortBy: string; sortOrder: 'asc' | 'desc'; searchQuery: string }
    }
  | { type: 'SET_EXPERIMENT_LIST_SORT_BY'; payload: string }
  | { type: 'SET_EXPERIMENT_LIST_SORT_ORDER'; payload: 'asc' | 'desc' }
  | { type: 'SET_EXPERIMENT_LIST_FILTERS'; payload: Record<string, any> }
  | { type: 'SET_EXPERIMENT_LIST_SEARCH_QUERY'; payload: string }
  | { type: 'RESET_EXPERIMENT_LIST_STATE' }
  | {
      type: 'SET_MICRO_LEARNING_LIST_STATE'
      payload: {
        statusFilter: string
        sortOrder: string
        searchQuery: string
        page: number
        columnSortBy: string
        columnSortOrder: 'asc' | 'desc'
        ouFilter: string
        areaFilter: string
        glaFilter: string
        scalePotentialFilter: '' | 'true' | 'false'
      }
    }
  | { type: 'RESET_MICRO_LEARNING_LIST_STATE' }
  | { type: 'SET_USER'; payload: User | null }
  | {
      type: 'SET_CHANGE_ROLE_REQUEST'
      payload: { organizationName: string; ou: string; function: string; requestReason: string } | null
    }
  | { type: 'SET_HAS_PENDING_ROLE_CHANGE_REQUEST'; payload: boolean }
  | {
      type: 'SET_ROLE_CHANGE_REQUEST_FILTER_STATE'
      payload: {
        status: string | undefined
        processed_by_user_id: string | undefined
        start_date: string | undefined
        end_date: string | undefined
        ou: string | undefined
      }
    }
  | { type: 'SET_NOTIFICATIONS'; payload: Notification[] | null }
  | { type: 'MARK_ALL_NOTIFICATIONS_AS_READ' }
  | { type: 'ADD_NOTIFICATION'; payload: Notification }
  | { type: 'DELETE_NOTIFICATION'; payload: string }
  | { type: 'SET_HAS_FETCH_NOTIFICATIONS_ERROR'; payload: boolean }
  | { type: 'SET_USER_FILTER'; payload: { role: string[]; search: string } | null }
  | {
      type: 'SET_USER_HISTORY_FILTER'
      payload: {
        search?: string
        action_type?: string
        start_date?: string
        end_date?: string
        processed_by_user_id?: string
      }
    }
  | { type: 'SET_LOOKUPS'; payload: { ous: string[] } }

const initialState: AppState = {
  chatHistoryLoadingState: ChatHistoryLoadingState.Loading,
  chatHistory: null,
  filteredChatHistory: null,
  currentChat: null,
  isCosmosDBAvailable: {
    cosmosDB: false,
    status: CosmosDBStatus.NotConfigured,
  },
  frontendSettings: null,
  feedbackState: {},
  isLoading: true,
  answerExecResult: {},
  shouldValidateForm: false,
  isUserActive: null,
  homeChatMessage: null,
  snackbarState: null,
  permissions: [],
  isNewChatDialogOpen: false,
  isRequestAccessDialogOpen: false,
  experimentListState: {
    ...initialExperimentListState,
  },
  microLearningListState: {
    ...initialMicroLearningListState,
  },
  user: null,
  changeRoleRequest: {
    organizationName: '',
    ou: '',
    function: '',
    requestReason: '',
  },
  hasPendingRoleChangeRequest: false,
  roleChangeRequestFilterState: initialRoleChangeRequestFilterState,
  notifications: null,
  hasFetchNotificationsError: false,
  userFilter: { role: [], search: '' },
  userHistoryFilter: { search: '', action_type: '', start_date: '', end_date: '', processed_by_user_id: '' },
  lookups: { ous: [] },
}

export const AppStateContext = createContext<
  | {
      state: AppState
      dispatch: React.Dispatch<Action>
    }
  | undefined
>(undefined)

type AppStateProviderProps = {
  children: ReactNode
}

export const AppStateProvider: React.FC<AppStateProviderProps> = ({ children }) => {
  const [state, dispatch] = useReducer(appStateReducer, initialState)
  const [bootstrapped, setBootstrapped] = React.useState<boolean>(false)
  const [appSecretReady, setAppSecretReady] = React.useState<boolean>(false)

  const { isConnected, sendMessage } = useNotifications(dispatch)

  useEffect(() => {
    if (isConnected && state && appSecretReady) {
      const getAllNotifications = () => {
        getNotifications()
          .then(notifications => {
            if (isNil(notifications)) {
              dispatch({ type: 'SET_HAS_FETCH_NOTIFICATIONS_ERROR', payload: true })
              return
            }
            dispatch({ type: 'SET_NOTIFICATIONS', payload: notifications?.notifications ?? [] })
            dispatch({ type: 'SET_HAS_FETCH_NOTIFICATIONS_ERROR', payload: false })
          })
          .catch(() => {
            dispatch({ type: 'SET_HAS_FETCH_NOTIFICATIONS_ERROR', payload: true })
          })
      }
      getAllNotifications()
    }
  }, [isConnected, dispatch, appSecretReady])

  useEffect(() => {
    const fetchBootstrap = async () => {
      try {
        const result = await bootstrap()
        setBootstrapped(result.success)

        if (result.status === 403) {
          dispatch({ type: 'SET_IS_USER_ACTIVE', payload: false })
        } else if (result.success === false) {
          dispatch({ type: 'UPDATE_CHAT_HISTORY_LOADING_STATE', payload: ChatHistoryLoadingState.Fail })
          dispatch({ type: 'SET_COSMOSDB_STATUS', payload: { cosmosDB: false, status: CosmosDBStatus.NotWorking } })
        } else {
          dispatch({ type: 'SET_IS_USER_ACTIVE', payload: true })
          dispatch({ type: 'SET_PERMISSIONS', payload: extractTruePermissions(result.user.permissions || {}) })
          dispatch({ type: 'SET_USER', payload: result.user })
          dispatch({ type: 'SET_HAS_PENDING_ROLE_CHANGE_REQUEST', payload: result.has_pending_role_change_request })
          dispatch({ type: 'SET_LOOKUPS', payload: result.lookups })
        }
      } catch (error) {
        console.error('Bootstrap failed:', error)
        dispatch({ type: 'UPDATE_CHAT_HISTORY_LOADING_STATE', payload: ChatHistoryLoadingState.Fail })
        dispatch({ type: 'SET_COSMOSDB_STATUS', payload: { cosmosDB: false, status: CosmosDBStatus.NotWorking } })
      }
    }
    fetchBootstrap()
  }, [])

  useEffect(() => {
    const initializeAppSecret = async () => {
      const secretOk = await fetchAppSecret()
      if (secretOk) {
        setAppSecretReady(true)
      } else {
        console.error('Failed to get app secret')
        setAppSecretReady(true)
      }
    }

    if (bootstrapped) {
      initializeAppSecret()
    }
  }, [bootstrapped])

  useEffect(() => {
    const fetchChatHistory = async (offset = 0): Promise<Conversation[] | null> => {
      const result = await historyList(offset)
        .then(response => {
          if (response) {
            dispatch({ type: 'FETCH_CHAT_HISTORY', payload: response })
          } else {
            dispatch({ type: 'FETCH_CHAT_HISTORY', payload: null })
          }
          return response
        })
        .catch(_err => {
          dispatch({ type: 'UPDATE_CHAT_HISTORY_LOADING_STATE', payload: ChatHistoryLoadingState.Fail })
          dispatch({ type: 'FETCH_CHAT_HISTORY', payload: null })
          console.error('There was an issue fetching your data.')
          return null
        })
      return result
    }

    const getHistoryEnsure = async () => {
      dispatch({ type: 'UPDATE_CHAT_HISTORY_LOADING_STATE', payload: ChatHistoryLoadingState.Loading })
      historyEnsure()
        .then(response => {
          if (response?.cosmosDB) {
            fetchChatHistory()
              .then(res => {
                if (res) {
                  dispatch({ type: 'UPDATE_CHAT_HISTORY_LOADING_STATE', payload: ChatHistoryLoadingState.Success })
                  dispatch({ type: 'SET_COSMOSDB_STATUS', payload: response })
                } else {
                  dispatch({ type: 'UPDATE_CHAT_HISTORY_LOADING_STATE', payload: ChatHistoryLoadingState.Fail })
                  dispatch({
                    type: 'SET_COSMOSDB_STATUS',
                    payload: { cosmosDB: false, status: CosmosDBStatus.NotWorking },
                  })
                }
              })
              .catch(_err => {
                dispatch({ type: 'UPDATE_CHAT_HISTORY_LOADING_STATE', payload: ChatHistoryLoadingState.Fail })
                dispatch({
                  type: 'SET_COSMOSDB_STATUS',
                  payload: { cosmosDB: false, status: CosmosDBStatus.NotWorking },
                })
              })
          } else {
            dispatch({ type: 'UPDATE_CHAT_HISTORY_LOADING_STATE', payload: ChatHistoryLoadingState.Fail })
            dispatch({ type: 'SET_COSMOSDB_STATUS', payload: response })
          }
        })
        .catch(_err => {
          dispatch({ type: 'UPDATE_CHAT_HISTORY_LOADING_STATE', payload: ChatHistoryLoadingState.Fail })
          dispatch({ type: 'SET_COSMOSDB_STATUS', payload: { cosmosDB: false, status: CosmosDBStatus.NotConfigured } })
        })
    }

    if (appSecretReady) {
      getHistoryEnsure()
    }
  }, [appSecretReady])

  useEffect(() => {
    if (appSecretReady) {
      frontendSettings()
        .then(response => {
          dispatch({ type: 'FETCH_FRONTEND_SETTINGS', payload: response as FrontendSettings })
        })
        .catch(_err => {
          console.error('There was an issue fetching frontend settings.')
        })
    }
  }, [appSecretReady])

  if (!appSecretReady) {
    return null
  }

  return <AppStateContext.Provider value={{ state, dispatch }}>{children}</AppStateContext.Provider>
}
